import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, TextButton } from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { verificationApi, extractApiError } from '../../lib/auth';
import PhoneVerification from '../../components/PhoneVerification';

export default function SignupEmailComplete() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token'); // 이메일 가입 링크 토큰
  const urlInviteToken = searchParams.get('inviteToken'); // 초대 토큰
  const urlEmail = searchParams.get('email'); // 초대 시 전달된 이메일

  const { signupWithEmail, isLoading, error, clearError } = useAuthStore();
  const { showToast } = useToastStore();

  // 토큰 검증 상태
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');

  // 폼 데이터
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneVerificationToken, setPhoneVerificationToken] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);

  // 상태
  const [localError, setLocalError] = useState('');

  // 초대 토큰 (URL 또는 localStorage에서)
  const [inviteToken, setInviteToken] = useState<string | null>(urlInviteToken);

  // 초대를 통한 가입인지 여부
  const isInviteSignup = !!urlInviteToken && !!urlEmail;

  // 초대 토큰 로드 (localStorage에서 - 이메일 가입 링크 통한 경우)
  useEffect(() => {
    if (!urlInviteToken) {
      const pendingToken = localStorage.getItem('pendingInviteToken');
      if (pendingToken) {
        setInviteToken(pendingToken);
      }
    }
  }, [urlInviteToken]);

  // 토큰 검증 (읽기 전용 - DB 수정 없음)
  useEffect(() => {
    // 초대를 통한 가입: 이메일 검증 토큰 불필요 (초대 이메일 수신 = 이메일 소유권 증명)
    if (isInviteSignup) {
      setEmail(urlEmail!);
      setTokenValid(true);
      setVerifying(false);
      return;
    }

    // 일반 이메일 가입: 이메일 검증 토큰 필요
    if (!urlToken) {
      setVerifying(false);
      setTokenValid(false);
      return;
    }

    setToken(urlToken);

    const checkToken = async () => {
      try {
        const result = await verificationApi.checkEmailSignupToken(urlToken);
        if (result.valid) {
          setTokenValid(true);
          if (result.email) {
            setEmail(result.email);
          }
        } else {
          setTokenValid(false);
        }
      } catch {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    checkToken();
  }, [urlToken, isInviteSignup, urlEmail]);

  // 비밀번호 유효성 검사 (서버와 동일한 규칙)
  const isValidPassword = (value: string) => {
    return value.length >= 8 && /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(value);
  };

  // 휴대폰 인증 완료
  const handlePhoneVerified = (token: string) => {
    setPhoneVerificationToken(token);
  };

  // 폼 검증
  const validateForm = (): boolean => {
    setLocalError('');
    clearError();

    if (!password) {
      setLocalError('비밀번호를 입력해주세요.');
      return false;
    }
    if (!isValidPassword(password)) {
      setLocalError('비밀번호는 8자 이상, 영문/숫자/특수문자를 포함해야 합니다.');
      return false;
    }
    if (password !== confirmPassword) {
      setLocalError('비밀번호가 일치하지 않습니다.');
      return false;
    }
    if (!name.trim() || name.trim().length < 2) {
      setLocalError('이름은 2자 이상 입력해주세요.');
      return false;
    }
    if (!phoneVerificationToken) {
      setLocalError('휴대폰 인증을 완료해주세요.');
      return false;
    }
    if (!termsAgreed) {
      setLocalError('이용약관에 동의해주세요.');
      return false;
    }

    return true;
  };

  // 회원가입 제출
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      let emailVerificationToken: string | undefined;

      // 초대를 통한 가입이 아닌 경우에만 이메일 토큰 검증
      if (!isInviteSignup && token) {
        const verifyResult = await verificationApi.verifyEmailToken(token);
        emailVerificationToken = verifyResult.verificationToken;
      }

      await signupWithEmail({
        email,
        emailVerificationToken,
        password,
        name: name.trim(),
        phone,
        phoneVerificationToken,
        inviteToken: inviteToken || undefined,
      });
      // 초대 토큰 정리
      localStorage.removeItem('pendingInviteToken');
      // 성공 토스트
      showToast('회원가입이 완료되었습니다. SafetyHub에 오신 것을 환영합니다!', 'success');
      // 초대 토큰이 있으면 이미 백엔드에서 자동 수락되었으므로 대시보드로 이동
      navigate('/', { replace: true });
    } catch (err) {
      // 복구 가능한 탈퇴 계정이 있는 경우 로그인 페이지로 안내
      const apiError = extractApiError(err);
      if (apiError.code === 'AUTH_SIGNUP_ACCOUNT_RECOVERABLE') {
        showToast(apiError.message, 'info');
        navigate('/login');
        return;
      }
      // verifyEmailToken 실패 시 에러 처리
      if (!isInviteSignup) {
        setLocalError('이메일 인증에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  const displayError = localError || error;

  // 로딩 화면
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="mt-4 text-ink-600">이메일 인증 확인 중...</p>
        </div>
      </div>
    );
  }

  // 토큰 무효 화면
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <Link to="/" className="inline-block">
              <h1 className="text-3xl font-bold text-brand-600 hover:text-brand-700">SafetyHub</h1>
            </Link>
            <div className="mt-8">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
                <svg
                  className="h-8 w-8 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="mt-4 text-2xl font-bold text-ink-900">링크가 유효하지 않습니다</h2>
              <p className="mt-4 text-ink-600">
                가입 링크가 만료되었거나 이미 사용되었습니다.
              </p>
              <p className="mt-2 text-sm text-ink-500">
                이메일 가입을 다시 시도해주세요.
              </p>
            </div>

            <div className="mt-8 space-y-3">
              <Link
                to="/signup/email"
                className="block w-full py-2 px-4 border border-transparent rounded-control shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700"
              >
                이메일로 다시 가입하기
              </Link>
              <Link
                to="/login"
                className="block w-full py-2 px-4 border border-ink-300 rounded-control shadow-sm text-sm font-medium text-ink-700 bg-white hover:bg-ink-50"
              >
                로그인으로 돌아가기
              </Link>
            </div>
          </div>

          <p className="mt-8 text-center text-xs text-ink-500">
            SafetyHub &copy; 2025. All rights reserved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* 헤더 */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-brand-600 hover:text-brand-700">SafetyHub</h1>
          </Link>
          <h2 className="mt-4 text-2xl font-bold text-ink-900">회원가입 완료하기</h2>
          <p className="mt-2 text-sm text-ink-600">
            {isInviteSignup
              ? '초대받은 이메일로 가입을 진행합니다. 나머지 정보를 입력해주세요.'
              : '이메일 인증이 완료되었습니다. 나머지 정보를 입력해주세요.'}
          </p>
        </div>

        {/* 이메일 표시 */}
        <div className="bg-green-50 rounded-card p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-green-800">
              이메일: <span className="font-medium">{email}</span>
              {isInviteSignup && <span className="text-green-600 ml-2">(초대받은 이메일)</span>}
            </p>
          </div>
        </div>

        {/* 초대 알림 */}
        {isInviteSignup && (
          <div className="bg-blue-50 border border-blue-200 rounded-card p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-800">조직 초대를 통한 가입</p>
                <p className="mt-1 text-sm text-blue-700">
                  회원가입 완료 시 자동으로 초대가 수락됩니다.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 에러 메시지 */}
        {displayError && (
          <div className="rounded-card bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{displayError}</p>
              </div>
            </div>
          </div>
        )}

        {/* 가입 폼 */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 비밀번호 */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-ink-700">
              비밀번호 <span className="text-red-500">*</span>
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
              placeholder="8자 이상, 영문/숫자/특수문자"
            />
            <p className="mt-1 text-xs text-ink-500">
              8자 이상, 영문/숫자/특수문자 포함
            </p>
          </div>

          {/* 비밀번호 확인 */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-ink-700">
              비밀번호 확인 <span className="text-red-500">*</span>
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
              placeholder="비밀번호 재입력"
            />
          </div>

          {/* 이름 */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-ink-700">
              이름 <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-ink-300 rounded-control focus:ring-brand-500 focus:border-brand-500"
              placeholder="홍길동"
            />
          </div>

          {/* 휴대폰 인증 */}
          <div className="pt-2">
            <PhoneVerification
              phone={phone}
              onPhoneChange={setPhone}
              onVerified={handlePhoneVerified}
              disabled={isLoading}
              skipDuplicateCheck
            />
          </div>

          {/* 이용약관 동의 */}
          <div className="flex items-start pt-2">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              checked={termsAgreed}
              onChange={(e) => setTermsAgreed(e.target.checked)}
              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-ink-300 rounded mt-0.5"
            />
            <label htmlFor="terms" className="ml-2 block text-sm text-ink-700">
              <span>
                {/* TODO: 문서 페이지 추가 후 Link로 복원 */}
                <TextButton
                  type="button"
                  className="underline-offset-2"
                  onClick={() => setTermsAgreed(!termsAgreed)}
                >
                  이용약관
                </TextButton>
                {' 및 '}
                <TextButton
                  type="button"
                  className="underline-offset-2"
                  onClick={() => setTermsAgreed(!termsAgreed)}
                >
                  개인정보처리방침
                </TextButton>
                에 동의합니다.
              </span>
            </label>
          </div>

          {/* 제출 버튼 */}
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading || !phoneVerificationToken}
            className="w-full"
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                가입 중...
              </span>
            ) : (
              '회원가입 완료'
            )}
          </Button>
        </form>

        {/* 푸터 */}
        <p className="mt-8 text-center text-xs text-ink-500">
          SafetyHub &copy; 2025. All rights reserved.
        </p>
      </div>
    </div>
  );
}
